import { useCartData } from "@/zustand/cartStore";
import { useProductData } from "@/zustand/productStore";

/*
 * PostHog checkout events (DEV-6713). PostHog itself is loaded by the theme's head
 * snippet; window.posthog is read at call time because it can still be the loader stub
 * when React mounts. Nothing here sends the buyer's name, contact, address or card data.
 *
 * Checkout V4 runs on every site, PostHog or not. The exports at the bottom do nothing while
 * PostHog is off for the site, and never throw: they run inside submit and payment error
 * branches, so a tracking error must not stop a payment flow.
 */

const STEPS = {
  contact_details: 1,
  shipping: 2,
  payment: 3,
};

const ERROR_MESSAGE_LENGTH = 200;

// The form-section steps in funnel order. A section completed out of order waits for the
// ones before it (e.g. the address done while a contact field is still invalid), so ordered
// funnels don't count the visitor as dropping off. Payment never waits: PayPal and wallets
// can pay without filling the form.
const SECTION_ORDER = ["contact_details", "shipping"];

const completedSteps = new Set();
const waitingSteps = new Set();
let pageViewSent = false;

const track = (name, properties, options) => {
  const posthog = window.posthog;

  if (posthog && typeof posthog.capture === "function") {
    posthog.capture(name, properties, options);
  }
};

// The cart total, mirroring useOrderCalculations: the stored cartTotal stays "0.00" when
// no order-summary drawer is mounted, so it is computed here from the selected package.
const cartValue = (selectedProduct, productData, { isDiscountApplied, warrantyState }) => {
  if (!selectedProduct) {
    return null;
  }

  const billedAmount = Number(selectedProduct.billed_amount) || Number(selectedProduct.amount) || 1;
  const unitPrice = isDiscountApplied ? selectedProduct.promo_discounted_price : selectedProduct.price;
  const warrantyPrice = productData?.multiply_price_by_amount
    ? Number(selectedProduct.warranty_price) * Number(selectedProduct.amount)
    : Number(selectedProduct.warranty_price);
  const shipping = isDiscountApplied ? selectedProduct.promo_shipping_price : selectedProduct.shipping_price;

  const total =
    (Number(unitPrice) || 0) * billedAmount + (warrantyState ? warrantyPrice || 0 : 0) + (Number(shipping) || 0);

  return Math.round(total * 100) / 100;
};

// The primary bundle product's SKU, as checkout records it on the order (the flagged one,
// else the first). The package's own sku is hidden with New Product Array Logic and can be
// out of date, so it is only the fallback.
const primarySku = (selectedProduct) => {
  const bundleProducts = (selectedProduct?.bundle_products || []).filter((product) => product?.bp_sku?.trim());
  const primary = bundleProducts.find((product) => product.primary_product) || bundleProducts[0];

  return primary?.bp_sku.trim() || selectedProduct?.sku || null;
};

const cartProperties = () => {
  const cart = useCartData.getState();
  const productData = useProductData.getState();
  const selectedProduct = cart.selectedProduct;

  return {
    sku: primarySku(selectedProduct),
    quantity: Number(selectedProduct?.amount) || null,
    cart_value: cartValue(selectedProduct, productData, cart),
    currency: productData?.currency_code || null,
  };
};

const trackPageView = () => {
  if (pageViewSent) {
    return;
  }

  pageViewSent = true;
  track("checkout_page_view", cartProperties());
};

// The checkout form's fields per section. A section is complete when all of its fields pass
// the form's own yup schema with the form's current context, the same rules as submit.
// Fields the schema doesn't know are skipped.
const SECTION_FIELDS = {
  contact_details: ["first_name", "last_name", "email", "phone"],
  shipping: ["country", "address", "city", "state", "zip"],
};

// The form section elements, for the focus-out check in usePostHogCheckout.
export const SECTION_STEPS = {
  "group-contacts": "contact_details",
  "group-delivery": "shipping",
};

let getFormState = null;

// The form registers a function returning { schema, values, context }, so sections are
// checked silently: trigger() would show errors while the visitor is still typing.
const registerForm = (getState) => {
  getFormState = getState;
};

const checkSection = async (stepName) => {
  const form = getFormState?.();
  const fields = (SECTION_FIELDS[stepName] || []).filter((field) => form?.schema?.fields?.[field]);

  if (!form || fields.length === 0) {
    return false;
  }

  const results = await Promise.all(
    fields.map((field) =>
      form.schema.validateAt(field, form.values, { context: form.context }).then(
        () => true,
        () => false
      )
    )
  );

  return results.every(Boolean);
};

const sendStep = (stepName, paymentMethod) => {
  completedSteps.add(stepName);
  waitingSteps.delete(stepName);
  track("checkout_step_completed", {
    step: STEPS[stepName],
    step_name: stepName,
    payment_method: paymentMethod || undefined,
    ...cartProperties(),
  });
};

// Once per step per page load. The payment step passes the method the visitor submitted with.
const trackStep = (stepName, paymentMethod) => {
  if (!STEPS[stepName] || completedSteps.has(stepName)) {
    return;
  }

  // Submitting straight from a form section beats its focus-out check, so the sections
  // that are complete go first and the steps stay in order.
  if (stepName === "payment") {
    completedSteps.add(stepName);
    Promise.all(SECTION_ORDER.map(checkSection))
      .then((complete) => {
        SECTION_ORDER.forEach((sectionStep, index) => complete[index] && trackStep(sectionStep));
        sendStep(stepName, paymentMethod);
      })
      .catch(() => {});

    return;
  }

  const sectionIndex = SECTION_ORDER.indexOf(stepName);

  if (sectionIndex > 0 && SECTION_ORDER.slice(0, sectionIndex).some((earlier) => !completedSteps.has(earlier))) {
    waitingSteps.add(stepName);
    return;
  }

  sendStep(stepName, paymentMethod);

  // Release sections that were waiting for this one.
  SECTION_ORDER.filter((waiting) => waitingSteps.has(waiting)).forEach((waiting) => trackStep(waiting));
};

const stepOfField = (fieldName) =>
  Object.keys(SECTION_FIELDS).find((stepName) => SECTION_FIELDS[stepName].includes(fieldName)) || "payment";

// errorType: payment_declined (the server answered ERROR), network (the request failed)
// or validation (the form has invalid fields). For validation only field names are sent.
const trackError = (paymentMethod, errorType, message, stepName = "payment") => {
  track("checkout_error", {
    step: STEPS[stepName] || null,
    step_name: stepName,
    error_type: errorType,
    error_message: typeof message === "string" ? message.slice(0, ERROR_MESSAGE_LENGTH) : null,
    payment_method: paymentMethod,
    ...cartProperties(),
  });
};

// The server answered ERROR. A decline it ties to a form field (e.g. the campaign's country
// restriction) is a validation error on that field, not a payment decline.
const trackDecline = (paymentMethod, data) => {
  if (data?.field) {
    trackError(paymentMethod, "validation", data.field, stepOfField(data.field));
  } else {
    trackError(paymentMethod, "payment_declined", data?.message);
  }
};

// Submit with invalid fields: field names only, never what was typed.
const trackValidationError = (paymentMethod, formErrors) => {
  const fields = Object.keys(formErrors || {});

  if (fields.length === 0) {
    return;
  }

  trackError(paymentMethod, "validation", fields.join(","), stepOfField(fields[0]));
};

// PostHog is on for this site only when the head snippet printed its config.
export const isPostHogEnabled = () => Boolean(window.ccPostHogConfig);

const guarded =
  (fn, fallback) =>
  (...args) => {
    if (!isPostHogEnabled()) {
      return fallback;
    }

    try {
      return fn(...args);
    } catch (error) {
      return fallback;
    }
  };

export const registerCheckoutForm = guarded(registerForm);
export const isSectionComplete = guarded(
  (stepName) => checkSection(stepName).catch(() => false),
  Promise.resolve(false)
);
export const trackCheckoutPageView = guarded(trackPageView);
export const trackCheckoutStep = guarded(trackStep);
export const trackCheckoutError = guarded(trackError);
export const trackCheckoutDecline = guarded(trackDecline);
export const trackCheckoutValidationError = guarded(trackValidationError);
